import { FormArray, FormControl, FormGroup } from '@angular/forms';

import { IFilterBatchSearch } from './filters';

export interface IOption {
  id: string;
  name: string;
}

export type TValue = string | number | null | undefined;

export interface IGenericResult<dados = any> {
  Summary?: ISearchSummary;
  Count: number;
  Data: dados;
  Message: string;
  ReturnCode: number;
  Id?: string;
}

export interface ITransferBatchMassCreateContractResult {
  QueryNumber: string;
  Number: string;
  Version: number;
  BeneficiaryName: string;
  BeneficiaryDocument: string;
}

export interface ITransferBatchMassCreateExcludedContractResult extends ITransferBatchMassCreateContractResult {
  Category: string; // 'AliquotaZerada' | 'CadastroParceiroIncompleto'
}

export interface ITransferBatchMassCreateNoteResult {
  BatchId: string;
  BatchNumber: string;
  Reason: string;
}

export interface ITransferBatchMassCreateItemResult {
  BeneficiaryName: string;
  BeneficiaryDocument: string;
  ContractCount: number;
  UsedExistingBatch?: boolean;
  Status: string;
  Message: string;
  BatchId?: string;
  BatchNumber: string;
  Contracts?: ITransferBatchMassCreateContractResult[];
  Notes?: ITransferBatchMassCreateNoteResult[];
}

export interface ITransferBatchMassCreateResult {
  TotalGroups: number;
  TotalAvailableMasters: number;
  ProcessedMastersCount: number;
  SuccessCount: number;
  CreatedNewCount?: number;
  AddedToExistingCount?: number;
  FailureCount: number;
  EmptyCount: number;
  CreatedBatches: ILot[];
  Items: ITransferBatchMassCreateItemResult[];
  ExcludedContracts?: ITransferBatchMassCreateExcludedContractResult[];
}

export interface ITransferAutomaticBatchExecution {
  ExecutionId: string;
  Status: 'Queued' | 'Running' | 'Completed' | 'Failed';
  StartedAt?: string;
  FinishedAt?: string;
  TriggeredByUsername: string;
  TotalCount: number;
  ProcessedCount: number;
  TotalContracts?: number;
  ProcessedContracts?: number;
  CurrentBeneficiaryName?: string;
  CurrentContractCount?: number;
  ErrorMessage?: string;
  Result?: ITransferBatchMassCreateResult;
}

export interface ITransferBatchMassCreatePreview {
  TotalLots: number;
  TotalMasters: number;
  TotalSubestabelecidos: number;
  ZeroFeeContractsCount: number;
  LotsToProcessNow: number;
  MastersToProcessNow: number;
}

export interface IGenericContract {
  EditableFields: IEditableField[];
  ContractCustomFields: IContractCustomField[];
  ContractId: string;
  CompanyId: number;
  FinancialInstitution: string;
  ContractDate: string;
  Amount: number;
  Number: string;
  Chanel: string;
  ContractType: string;
  Client: string;
  QueryNumber: string;
  ContractStatus: string;
  Status: string;
  StatusOverride: string;
  Bpo: string;
  Product: string;
  ClientDocument: string;
  Fee: number;
  Subsidiary: string;
  Store: string;
  Error: boolean;
  MsgError: string;
  Processed: boolean;
  Editing: boolean;
  Incomplete: boolean;
  Version: number;
  Draft: boolean;
  Aproved: boolean;
  Current: boolean;
  ImportDate: string;
  CancelPartner: boolean;
  CancelCompany: boolean;
  Beneficiaries: IBeneficiary[];
  LogsChangePartnerContract: IContractPartnerChangeLog[];
  ContractEventLogs: IContractEventLog[];
  StageName: string;
  Transfers: IBeneficiary[];
  OperationType: string;
  PaymentDate: string;
  EstimatedPaymentDate: string;
  BeneficiaryFee: number;
  Comments: string;
  Invoice: IInvoiceData;
  AgreementDocumentChangeCommission: string;
  JustificationChangeCommission: string;
  [key: string]:
    | string
    | number
    | boolean
    | IEditableField[]
    | IContractPartnerChangeLog[]
    | IContractEventLog[]
    | IContractCustomField[]
    | IBeneficiary[]
    | IInvoiceData;
}
export const FieldsToIgnore = [
  'EditableFields',
  'ContractCustomFields',
  'Beneficiaries'
];

export interface IChangeCommission {
  ContractId: string;
  AgreementDocumentChangeCommission: string;
  JustificationChangeCommission: string;
}

export interface IGenericContractFormGroup {
  Amount: FormControl<number | null>;
  Aproved: FormControl<boolean | null>;
  BatchDetails: FormGroup<IBatchDetailsFormGroup>;
  Beneficiaries: FormArray<FormGroup<IBeneficiaryFormGroup>>;
  bpo: FormControl<string | null>;
  CancelCompany: FormControl<boolean | null>;
  CancelPartner: FormControl<boolean | null>;
  carteiraCredito: FormControl<string | null>;
  Chanel: FormControl<string | null>;
  cliente: FormControl<string | null>;
  cnpj: FormControl<string | null>;
  Comments: FormControl<string | null>;
  CompanyId: FormControl<number | null>;
  ContractDate: FormControl<string | null>;
  ContractId: FormControl<string | null>;
  ContractStatus: FormControl<string | null>;
  ContractType: FormControl<string | null>;
  contrato: FormControl<string | null>;
  Current: FormControl<boolean | null>;
  Draft: FormControl<boolean | null>;
  dtAssinatura: FormControl<string | null>;
  dtAssinaturaNovoContrato: FormControl<string | null>;
  dtFaturamento: FormControl<string | null>;
  dtProducao: FormControl<string | null>;
  dtStatus: FormControl<string | null>;
  Editing: FormControl<boolean | null>;
  Error: FormControl<boolean | null>;
  fgts: FormControl<number | null>;
  FinancialInstitution: FormControl<string | null>;
  imobiliaria: FormControl<string | null>;
  ImportDate: FormControl<string | null>;
  Incomplete: FormControl<boolean | null>;
  InvoiceBeneficiaries: FormGroup<IBeneficiaryFormGroup>;
  MsgError: FormControl<string | null>;
  nf: FormControl<string | null>;
  nLote: FormControl<string | null>;
  Number: FormControl<string | null>;
  porcentagemFaturado: FormControl<number | null>;
  Processed: FormControl<boolean | null>;
  QueryNumber: FormControl<string | null>;
  rede: FormControl<string | null>;
  Status: FormControl<string | null>;
  statusFat: FormControl<string | null>;
  tipoFaturamento: FormControl<string | null>;
  tipoVenda: FormControl<string | null>;
  TransferBeneficiaries: FormGroup<IBeneficiaryFormGroup>;
  valorFaturado: FormControl<number | null>;
  valorVenda: FormControl<number | null>;
  Version: FormControl<number | null>;
  vgv: FormControl<number | null>;
}

export interface IEditableField {
  Entity: string;
  Field: string;
}

export interface IContractCustomField {
  CustomFieldId: number;
  FieldName: string;
  Value: string;
  Label?: string;
}

export interface IBeneficiary {
  Amount?: number;
  BeneficiaryDocument?: string;
  BeneficiaryFee?: number;
  BeneficiaryId?: number;
  BeneficiaryName?: string;
  BeneficiaryType: string;
  BeneficiaryTypeId?: string;
  Comission?: number;
  ContractId?: string;
  Document?: string;
  EstimatedPaymentDate?: string;
  Fee?: number;
  Name?: string;
  PaymentDate?: string;
  Status?: string;
}

export interface IContractPartnerChangeLog {
  ChangeUserName?: string;
  ContractId?: string;
  LogChangePartnerContractId?: string;
  NewCNPJPartner?: string;
  NewNamePartner?: string;
  OldCNPJPartner: string;
  OldNamePartner?: string;
  ChangeDateTime?: string;
  ChangeDateTimeFormatted?: string;
}

export interface IContractEventLog {
  ContractEventLogId: string;
  BatchId?: string;
  ParentBatchId?: string;
  ContractId: string;
  Event: string;
  BatchNumber?: number;
  ParentBatchNumber?: string;
  ContractNumber: number;
  Username: string;
  CreateDateString: string;
}

export interface IBeneficiaryFormGroup {
  BeneficiaryId?: FormControl<number | null>;
  ContractId?: FormControl<string | null>;
  Name: FormControl<string | null>;
  Document: FormControl<string | null>;
  BeneficiaryType?: FormControl<string | null>;
  BeneficiaryTypeId?: FormControl<string | null>;
  Fee?: FormControl<number | null>;
  EstimatedPaymentDate?: FormControl<string | null>;
  PaymentDate?: FormControl<string | null>;
}

export interface IBatchDetails {
  BatchNumber: string;
  PaymentDate: string;
  BatchStatus: string;
  Amount: number;
  ContractType: string;
  ContractAmount: number;
  ContractAmountPercent: number;
  Nfe: string;
  VGC?: number;
  InvoiceDate: string;
}

/**
 * Escopo confirmado contra o banco do backend (Invoice_Batch/Invoice_BatchContract/
 * Invoice_Contract/Invoice_Invoice/Invoice_Reverse): so tipos Faturamento e Estorno tem
 * dado real hoje. Refaturamento fica fora ate a funcionalidade existir no backend.
 */
export type ContractMovementType = 'Faturamento' | 'Estorno' | 'Refaturamento';

export interface IContractMovement {
  Tipo: ContractMovementType;
  InvoicingStatus?: string;
  Contrato: string;
  QueryNumber?: string;
  Version?: number;
  Lote: string;
  LoteStatus?: string;
  Nf?: string;
  DataEmissao: string;
  Valor: number;
  Percentual?: number;
  Comissao?: number;
  Status: string;
}

export interface IContractCurrentSituation {
  StatusFaturamento: string;
  TipoFaturamento: string;
  NotaFiscalVigente: string;
  DataEmissaoNf: string;
  LoteFaturamento: string;
  LoteRepasse: string;
  StatusNoBanco: string;
  ContratoOrigem: string;
}

export interface IContractHistory {
  LogsChangePartnerContract: IContractPartnerChangeLog[];
  ContractEventLogs: IContractEventLog[];
}

export interface IBatchDetailsFormGroup {
  BatchNumber: FormControl<string>;
  PaymentDate: FormControl<string>;
  InvoiceDate: FormControl<string>;
  BatchStatus: FormControl<string>;
  BatchAmount: FormControl<number | null>;
  ContractType: FormControl<string>;
  ContractAmount: FormControl<number | null>;
  ContractAmountPercent: FormControl<number | null>;
  Nfe: FormControl<string>;
  VGC: FormControl<number | null>;
  BatchTransferNumber: FormControl<string>;
}

export interface ITransfersAmount {
  TransfersAmountTotal: number;
}
export interface IInvoicesAmount {
  InvoicesAmountTotal: number;
  ToReceive: number;
  Cancel: number;
  CancelApplied: number;
  PassedOn: number;
}

export interface ITransfersFee {
  CompanyId: string;
  Number: string;
  Chanel: string;
  FinancialInstitution: string;
  Status: string;
  ContractType: string;
  Amount: number;
  Transfers: ITransfersData[];
  id: string;
}

export interface ITransfersData {
  BeneficiaryDocument: string;
  BeneficiaryName: string;
  BeneficiaryType: string;
  BeneficiaryFee: number;
  Amount: number;
}

export interface IInvoicesFee {
  ContractId: string;
  CompanyId: string;
  Number: string;
  Chanel: string;
  FinancialInstitution: string;
  Status: string;
  ContractType: string;
  Amount: number;
  BeneficiaryDocument: string;
  BeneficiaryName: string;
  BeneficiaryType: string;
  BeneficiaryFee: number;
  Error: boolean;
  Invoice: IInvoiceData;
  CreateDate: string;
  UpdateDate: string;
  Active: boolean;
  id: string;
}

export interface IInvoiceData {
  InvoiceId: string;
  ContractId: string;
  Amount: number;
  CreateDate?: string;
  UpdateDate?: string;
  Active?: boolean;
}

export enum TabTypeEnum {
  Link = 1,
  Render = 2,
  Modal = 3
}

export enum ConfigTypeEnum {
  Email = 1,
  Parameter,
  TransferConfig,
  StoreConfig,
  ReverseConfig
}

export enum DetailLotEnum {
  Transfer = 1,
  Modal = 2,
  Invoicing = 3
}

export interface IPagination {
  Skip: number;
  Take: number;
}
export interface ILot {
  Active?: boolean;
  AmountTotal: number;
  AutomaticEmail?: boolean;
  Bank: string;
  BatchId: number;
  BatchNumber: string;
  BatchStatus?: string;
  BPO?: string;
  Calculations?: string;
  CancelInvoideDate?: string;
  CancelInvoiceNumber?: string;
  Channel: string;
  TotalReversed?: number;
  TotalReversedNegativo?: number;
  DocuSignContractId?: number;
  Reverses?: IContractLot[];
  ReverseBalance?: number;
  Checked: boolean;
  Contracts?: IContractLot[];
  CreateDate: string;
  EconomicGroupAutorizationFile?: string;
  EmailInvoiceDate?: string;
  EstimatedPaymentDate?: string;
  FinancingAmountTotal?: number;
  HasEconomicGroup?: boolean;
  TwoBusinessDaysBeforeEstimatedPaymentDate?: boolean;
  HasOffice?: boolean;
  Integrated?: boolean;
  IntegrationError?: boolean;
  IntegrationDate?: string;
  IntegrationMsg?: string;
  InvoiceDate?: string;
  InvoiceNumber?: string;
  IsInvoiced?: boolean;
  RpaDate?: string;
  RpaNumber?: string;
  HasRpa?: boolean;
  IsMultipleBanks?: boolean;
  IsMultiplePartners?: boolean;
  NumberCurrentBatch?: string;
  NumberOfContracts: number;
  OfficeDocument?: string;
  OfficeName?: string;
  OrderDate?: string;
  OrderNumber?: string;
  Partner: string;
  Partners?: ILot[];
  PaymentDate?: string;
  ReceiptDate?: string;
  PartnerDocument?: string;
  SapCode?: string;
  SapIntegrationError?: boolean;
  SapPaymentStatus?: string;
  OmiePaymentStatus?: string;
  CancelamentoSapCode?: string;
  StatusOfOnlending?: string;
  Observacao?: string;
  Comment?: string;
  Type?: 'Manual' | 'Automatic';
  EmailTransferDate?: string;
  EmailEstimatedPaymentDate?: string;
  LegacyBatchNumber?: string;
  Bpo?: string;
  OperationType?: string;
  HasPaymentProof?: boolean;
  SucessOperation?: boolean;
  FirstContractIsSubstabelecido?: boolean;
  IsSimplesNacional?: boolean | null;
  IsSIMEI?: boolean | null;
  CalcularIss?: boolean;
  ServiceCodeId?: string | null;
  ServiceCodeValue?: string | null;
  ServiceCodeDescription?: string | null;
  IssRate?: number | null;
  IrRate?: number | null;
  PisRate?: number | null;
  CofinsRate?: number | null;
  CsllRate?: number | null;
  InssRate?: number | null;
  IssAmount?: number;
  IrAmount?: number;
  PisAmount?: number;
  CofinsAmount?: number;
  CsllAmount?: number;
  InssAmount?: number;
  TotalWithholdingAmount?: number;
  NetAmount?: number;
  IsentoRetencao?: boolean;
}

export interface IReverseLot {
  Amount: number;
  Batch: any;
  BatchId: string;
  ContractId: string;
  ReverseId: string;
  Contract: IContractLot;
}

export interface IContractLot {
  Active: boolean;
  Amount: number;
  Beneficiaries?: { Name: string; Type: string; Fee: number }[];
  BeneficiaryDocument?: string;
  BeneficiaryFee: number;
  BeneficiaryName: string;
  BeneficiaryType: string;
  Chanel: string;
  CompanyId?: string;
  ContractId: string;
  ContractIds?: string[];
  ContractType: string;
  CreateDate: string;
  Error?: boolean;
  MsgError?: string;
  FinancialInstitution: string;
  Number: string;
  Status: string;
  Invoice?: IInvoiceData;
  InvoiceAmount?: number;
  StageName?: string;
  ContractDate?: string;
  QueryNumber: string;
  ContractStatus?: string;
  Processed?: boolean;
  Editing?: boolean;
  Incomplete?: boolean;
  Version?: number;
  Draft?: boolean;
  Aproved?: true;
  Current?: true;
  ImportDate?: string;
  CancelPartner?: boolean;
  CancelCompany?: boolean;
  Cancel?: boolean;
  Beneficiearies?: IBeneficiary[];
  TransferAmount?: number;
  TransferAmountNegativo?: number;
  OperationType?: string;
  Client?: string;
  ClientDocument?: string;
  ProductionDate?: string;
  InvoiceBatchNumber?: string;
  InvoiceBatchPaid?: boolean;
  InvoiceBatchStatus?: string;
  IsDelete?: boolean;
  Subsidiary?: string;
  SubestabelecidoContractId?: string;
  SubestabelecidoContract?: IContractLot;
  IsSubestabelecidoContract?: boolean;
  ToReverse?: boolean;
  StatusOverride?: string;
  ProductId?: string;
  Product?: string;
  ProductName?: string;
  productId?: string;
  product?: string;
  productName?: string;
}

export interface IContractsInLot {
  BatchNumber: string;
  ContractsIds?: Array<string>;
  ContractsExcludesIds?: Array<string>;
  ContractsInsertsIds?: Array<string>;
  ContractsCancelledIds?: Array<string>;
}
export interface IHeaderItem {
  label: string;
  flexSize: number;
  isButton?: boolean;
  justify?: string;
  isOrder?: boolean;
  orderValue?: number;
  mask?: string;
  currency?: boolean;
  name?: string;
}

export interface IBank {
  BankId: string;
  Document: string;
  Name: string;
}

export interface IChannel {
  ChannelId: string;
  Name: string;
}

export interface ISendBatchEmail {
  BatchIds: string[];
  ExceptionsBatchIds: string[];
  Filter: IFilterBatchSearch;
  SendAll: boolean;
}

export interface IBatchEmailConfig {
  BankId: string;
  Name: string;
  AutomaticEmail: boolean;
}

export enum BaseUrls {
  Contract = 'baseUrlContractReport',
  Transfers = 'baseUrlTransfers',
  TransfersLots = 'baseUrlBatchTransfers',
  InvoicesReport = 'baseUrlInvoicesReport',
  InvoicesLots = 'baseUrlInvoicesLots'
}

export interface ICustomSelect {
  name: string;
  id: string;
}

export type HeaderTitle =
  | 'Esteira de Apuração'
  | 'Faturamento dos Contratos'
  | 'Repasse de Comissão'
  | 'Configurações'
  | 'Comissão de Faturamento'
  | 'Comissao Faturamento'
  | 'Códigos de Serviço e Tributação';

export enum ActionEnum {
  Create = 'create',
  Delete = 'delete',
  Edit = 'edit',
  View = 'view'
}

export type ActionType = 'create' | 'delete' | 'edit' | 'view';

export enum Mode {
  Transfer = 'transfer',
  Invoicing = 'invoicing',
  Commission = 'commission',
  Contracts = 'contracts'
}

export type ModeType = 'transfer' | 'invoicing' | 'commission' | 'contracts';

export enum ContractStatus {
  Registrado = 'registrado',
  PreFaturado = 'pre-faturado',
  Aprovacao = 'approval',
  AprovacaoContrato = 'aprovacao'
}

export enum LotStatus {
  Aprovacao = 'aguardando aprovacao',
  AprovacaoContratos = 'aguardando aprovacao dos contratos',
  Reprovado = 'reprovado',
  Andamento = 'em andamento'
}

export enum BeneficiaryTypeEnum {
  Subsidiaria = 'subsidiaria',
  Credimorar = 'credimorar',
  Parceiro = 'parceiro'
}

export type TransferConfigType = 'manual' | 'automatic';

export enum TransferConfigEnum {
  Manual = 'manual',
  Automatic = 'automatic'
}

export interface ISearchSummary {
  TotalBatchs: number;
  TotalContracts: number;
  BatchAmountSum: number;
  BatchPagedAmountSum: number;
}

export interface IHeaderSummary {
  label: string;
  value: any;
}

export interface IKpiItem {
  label: string;
  value: string | number;
  tone?: 'default' | 'brand' | 'danger';
  hint?: string;
}

export interface IResponse {
  success: boolean;
  message: string;
  data: any;
}
